import { Component, computed, effect, inject, input, OnInit, output, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { BreadcrumbService } from 'xng-breadcrumb';
import { fb, routeParams, translate } from '../../../../../modules/shared/shared.global-service';
import { BreadcrumbsEnum } from '../../../../../modules/shared/models/breadcrumbs.enum';
import { FolderSickNotesDetailPageStore } from './folder-sick-notes-detail-page.store';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { ActionButtonsComponent } from '../../../../kit/action-buttons/action-buttons.component';
import { Validators } from '@angular/forms';
import { RadioInputComponent } from '../../../../kit/input/radio-input/radio-input.component';
import {
    FolderSickNotesSubmissionHistoryModalComponent,
} from '../folder-sick-notes-submission-history-modal/folder-sick-notes-submission-history-modal.component';
import {
    FolderSickNotesDoctorReportModalComponent,
} from '../folder-sick-notes-doctor-report-modal/folder-sick-notes-doctor-report-modal.component';
import { CheckInputComponent } from '../../../../kit/input/check-input/check-input.component';
import { TextInputComponent } from '../../../../kit/input/text-input/text-input.component';
import { DateInputComponent } from '../../../../kit/input/date-input/date-input.component';
import { dateFromTime, dateify } from '../../../../../modules/shared/helpers/date.helper';
import { AddressDetailObject, FolderGridItemObject, RegionalStructureObject, UpdatePersonInput } from '@it2go/types';
import {
    CsszInsuranceAdministratorEnum,
    csszInsuranceAdministratorEnumOptions,
} from '@libs/types/src/services/enum/cssz-insurance-administrator.enum';
import { FolderSickNotesCreateInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.create-input';
import { mapAddress } from '../../../../../modules/shared/helpers/map-address.helper';
import { SubscriptionComponent } from '../../../../../modules/shared/helpers/subscription.component';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { contentGlobalSuccess } from '../../../../../modules/content/content.error';
import {
    FolderSickNotesDoctorRequestModalComponent,
} from '../folder-sick-notes-doctor-request-modal/folder-sick-notes-doctor-request-modal.component';
import { DiagnosisSelectInputComponent } from '../../../diagnosis-select-input/diagnosis-select-input.component';
import { FilterBuilder } from '../../../../../modules/content/helper/filter.builder';
import { csszDateFormat, csszTimeFormat } from '../utils-CSSZ';
import { FolderSickNotesPtdpnInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.ptdpn-input';
import { SwitchInputComponent } from '../../../../kit/input/switch-input/switch-input.component';
import { Router } from '@angular/router';
import { FolderSickNotesCloseInput } from '@libs/types/src/services/input-type/cssz/folder-sick-notes.close-input';
import { folderFrag } from '../../../../../services/types/folder.types';
import { SelectInputComponent } from '../../../../kit/input/select-input/select-input.component';
import { folderUri } from '../../../../../utils/router.utils';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { AddressInputComponent } from '../../../../kit/input/address-input/address-input.component';

@Component({
    selector: 'app-folder-sick-notes-detail',
    imports: [
        TranslatePipe,
        ButtonComponent,
        ActionButtonsComponent,
        RadioInputComponent,
        CheckInputComponent,
        TextInputComponent,
        DateInputComponent,
        TooltipModule,
        DiagnosisSelectInputComponent,
        SwitchInputComponent,
        SelectInputComponent,
        AddressInputComponent,
    ],
    templateUrl: './folder-sick-notes-detail-page.component.html',
    styleUrl: './folder-sick-notes-detail-page.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [FolderSickNotesDetailPageStore],
})
export class FolderSickNotesDetailPageComponent extends SubscriptionComponent implements OnInit {
    protected readonly store = inject(FolderSickNotesDetailPageStore);
    protected readonly dialogService = inject(BaseDialogService);
    public readonly router = inject(Router);
    private readonly breadcrumbs = inject(BreadcrumbService);


    // Are set only when component is wrapped in modal
    public folderIdInput = input<number>(0);
    public sickNoteIdInput = input<number>(0);
    public closed = output<boolean>();

    protected form = fb.group({
        decisionNumber: fb.control<string | null>(null, Validators.required),
        sszCode: fb.control<string | null>(null, Validators.required),
        correctiveFilling: fb.control<boolean>(false),

        surname: fb.control<string | null>(null, Validators.required),
        name: fb.control<string | null>(null, Validators.required),
        titles: fb.control<string | null>(null),

        birthDate: fb.control<Date | null>(null, Validators.required),
        insuranceNumber: fb.control<string | null>(null, Validators.required),

        address: fb.control<AddressDetailObject | null>(
            null,
            Validators.required,
        ),
        addition: fb.control<string | null>(null),
        phone: fb.control<string | null>(null),
        email: fb.control<string | null>(null),

        employmentId: fb.control<string | null>(null),
        employerName: fb.control<string | null>(null, Validators.required),
        employerVariableSymbol: fb.control<string | null>(null),
        profession: fb.control<string | null>(null, Validators.required),
        employerAddress: fb.control<AddressDetailObject | null>(
            null,
            Validators.required,
        ),

        walkoutsFromDate: fb.control<Date | null>(null),
        walkoutsFrom1: fb.control<string | null>(null),
        walkoutsTo1: fb.control<string | null>(null),
        walkoutsFrom2: fb.control<string | null>(null),
        walkoutsTo2: fb.control<string | null>(null),

        insuranceAdministrator: fb.control<CsszInsuranceAdministratorEnum>(
            CsszInsuranceAdministratorEnum.CSSZ,
            Validators.required,
        ),

        diagnosisCode: fb.control<string | null>(null, Validators.required),
        incapacityStartDate: fb.control<Date>(new Date(), Validators.required),
        issueDate: fb.control<Date>(new Date(), Validators.required),

        injury: fb.control<boolean>(false),
        workInjury: fb.control<boolean>(false),
        injuryFromAnotherPerson: fb.control<boolean>(false),
        usingAlcoholOrDrugs: fb.control<boolean>(false),

        doctorOrigin: fb.control<string | null>(null, Validators.required),
    });

    protected ptdpnForm = fb.group({
        confirmationDurationDate: fb.control<Date | null>(
            null,
            Validators.required,
        ),
        confirmationIssueDate: fb.control<Date>(new Date(), Validators.required),
        confirmingDoctor: fb.control<string | null>(null, Validators.required),
        controlDate: fb.control<Date | null>(null),
    });

    protected finalForm = fb.group({
        finalDiagnosisCode: fb.control<string | null>(null, Validators.required),
        incapacityEndDate: fb.control<Date | null>(null, Validators.required),
        issueEndDate: fb.control<Date | null>(null, Validators.required),
        decidingDoctor: fb.control<string | null>(null, Validators.required),
    });

    protected prefix = 'sickNotes.detail.form.';

    protected readonly errorMessage = signal('');

    protected readonly partOneFilled = signal(false);
    protected ptdpnFilled = false;
    protected readonly finalFilled = signal(false);

    protected sickNoteId = computed(() => +routeParams.sickNoteId() || this.sickNoteIdInput());
    protected folder = computed(() => this.store.folder() || this.store.data.getFolder());

    constructor() {
        super();

        effect(() => {
            if (this.sickNoteId() !== 0) {
                this.breadcrumbs.set(
                    `@${BreadcrumbsEnum.FolderSickNote}`,
                    `${BreadcrumbsEnum.FolderSickNote}|${this.sickNoteId()}`,
                );
                this.store.api
                    .getSickNotes(
                        new FilterBuilder()
                            .where('id', this.sickNoteId())
                            .filter(),
                    )
                    .then((res) => {
                        const sn = res.items[0];
                        if (sn) {
                            this.form.patchValue({
                                ...sn,
                                address: mapAddress(sn.address),
                                employerAddress: mapAddress(sn.employerAddress),
                                birthDate: dateify(sn.birthDate),
                                issueDate: dateify(sn.issueDate),
                                walkoutsFrom1: dateFromTime(sn.walkoutsFrom1),
                                walkoutsTo1: dateFromTime(sn.walkoutsTo1),
                                walkoutsFrom2: dateFromTime(sn.walkoutsFrom2),
                                walkoutsTo2: dateFromTime(sn.walkoutsTo2),
                                walkoutsFromDate: dateify(sn.walkoutsFromDate),
                                incapacityStartDate: dateify(
                                    sn.incapacityStartDate,
                                ),
                            });
                            this.partOneFilled.set(true);
                            if (sn.confirmationIssueDate) {
                                this.ptdpnForm.patchValue({
                                    confirmationDurationDate: dateify(sn.confirmationDurationDate),
                                    confirmationIssueDate: dateify(sn.confirmationIssueDate),
                                    confirmingDoctor: sn.confirmingDoctor,
                                    controlDate: dateify(sn.controlDate),
                                });
                                this.ptdpnFilled = true;
                            }
                            if (sn.issueEndDate) {
                                this.finalForm.patchValue({
                                    issueEndDate: dateify(sn.issueEndDate),
                                    incapacityEndDate: dateify(sn.incapacityEndDate),
                                    finalDiagnosisCode: sn.diagnosisCode,
                                    decidingDoctor: sn.decidingDoctor,
                                });
                                this.finalFilled.set(true);
                            }
                        }
                    });
            }
        });

        effect(() => {
            const folder = this.folder();
            if (folder && this.sickNoteId() === 0) {
                this.form.patchValue({
                    surname: folder.person.surname,
                    name: folder.person.name,
                    titles: [
                        folder.person.titleAfterName,
                        folder.person.titleBeforeName,
                    ]
                        .filter(Boolean)
                        .join(', '),
                    insuranceNumber: folder.person.insuranceNumber,
                    birthDate: dateify(folder.person.birthdate),
                    profession: folder.person.profession,
                });
                untracked(() => {
                    this.form.patchValue({
                        // nevim proc se to na tomto jebe a prijde mi to lepsi nez allowSignalWrites
                        address:
                            folder.person.addresses[0]?.address &&
                            mapAddress(folder.person.addresses[0]?.address),
                    });
                });
                this.getDecisionNumber();
                this.getEmployers(folder);
                this.ptdpnForm.disable();
            }
        });
        effect(() => {
            const currentUser = this.store.globalGlobalStore.loggedUserStore.currentUser();
            if (currentUser) {
                this.form.patchValue({
                    doctorOrigin: currentUser.username,
                });
                this.ptdpnForm.patchValue({
                    confirmingDoctor: currentUser.username,
                });
                this.finalForm.patchValue({
                    decidingDoctor: currentUser.username,
                });
            }
        });
        effect(() => {
            // remembered ssz code from last sick note
            const lastSickNote = this.store.lastSickNote;
            if (lastSickNote) {
                this.form.patchValue({
                    sszCode: lastSickNote.sszCode,
                });
            }
        });
        this.subs.push(
            this.form.controls.insuranceAdministrator.valueChanges.subscribe(
                (value) => {
                    this.onInsuranceAdministratorChange(value!);
                },
            ),

            // when the employer address changes, try to find the SSZ code
            this.form.controls.employerAddress.valueChanges.subscribe(
                async (address: AddressDetailObject | null) => {
                    if (!this.store.sszSszRegions?.length) return;
                    if (!address?.regionalStructure?.length) return;
                    /* REGIONAL STRUCTURE EXAMPLE
                        { name: "834/17", type: "regional.address" }
                        { name: "Václavské náměstí", type: "regional.street" }
                        { name: "Nové Město", type: "regional.municipality_part" }
                        { name: "Praha 1", type: "regional.municipality_part" }
                        { name: "Praha", type: "regional.municipality" }
                        { name: "okres Hlavní město Praha", type: "regional.region" }
                        { name: "kraj Hlavní město Praha", type: "regional.region" }
                    */
                    let sszCode = undefined;
                    for (const item of address.regionalStructure) {
                        // stupid name matching, wish there was a better way
                        if (item.name.startsWith('okres') || item.name.startsWith('Praha ')) {
                            const shortened = item.name.replace('okres ', '').trim();
                            sszCode = this.store.sszSszRegions.find(
                                (sszRegion) => sszRegion.name.toLowerCase() === shortened.toLowerCase(),
                            )?.code;
                            if (sszCode) {
                                break;
                            }
                        }
                    }
                    if (sszCode) {
                        this.form.patchValue({
                            sszCode,
                        });
                        this.store.sszRegionScroll.prefetchItems([[{
                            column: 'code',
                            operator: 'EQ',
                            value: [sszCode],
                        }]]);
                    }
                },
            ),
        );
    }

    ngOnInit() {
        if (this.folderIdInput()) {
            const frag = `
                ${folderFrag}
                serviceId
            `;
            this.store.api.getFolder({
                id: this.folderIdInput(),
            }, frag).then();
        }
    }

    protected async saveAndSend() {
        const data = this.formPartOne();
        if (!data) return;

        if (this.partOneFilled()) {
            if (data.correctiveFilling) {
                const update = {
                    id: this.sickNoteId(),
                    ...data,
                };
                const res = await this.store.api.updateSickNote(update);
                if (res.ok) {
                    contentGlobalSuccess.next('common.created');
                }

            } else {
                const closeData = this.formPartTwo(data);
                if (!closeData) return;
                const res = await this.store.api.close(closeData);
                if (res.ok) {
                    contentGlobalSuccess.next('common.created');
                }
            }
        } else {
            const res = await this.store.api.createSickNote(data);
            if (res.ok) {
                contentGlobalSuccess.next('common.updated');
            }
        }

        this.close(true);
    }

    protected close(update = false) {
        if (this.folderIdInput()) {
            this.closed.emit(update);
        } else {
            this.router.navigate([folderUri(routeParams.folderId(), this.store.folder()?.serviceId, 'sick-notes')]).then();
        }
    }

    protected savePtdpn() {
        if (this.form.isInvalid()) return;
        if (this.ptdpnForm.isInvalid()) return;

        const data: FolderSickNotesPtdpnInput = {
            sickNoteId: this.sickNoteId(),
            serviceId: this.folder()!.serviceId,
            sszCode: this.form.value.sszCode!,
            insuranceAdministrator: this.form.value.insuranceAdministrator!,
            decisionNumber: this.form.value.decisionNumber!,
            name: this.form.value.name!,
            surname: this.form.value.surname!,
            titles: this.form.value.titles,
            insuranceNumber: this.form.value.insuranceNumber!,
            birthDate: csszDateFormat(this.form.value.birthDate)!,
            phone: this.form.value.phone || '',
            email: this.form.value.email || '',
            confirmationIssueDate: csszDateFormat(
                this.ptdpnForm.value.confirmationIssueDate,
            )!,
            confirmationDurationDate: csszDateFormat(
                this.ptdpnForm.value.confirmationDurationDate,
            )!,
            confirmingDoctor:
                this.ptdpnForm.controls.confirmingDoctor.value || '',
            controlDate:
                csszDateFormat(this.ptdpnForm.controls.controlDate.value) ||
                null,
        };
        this.store.api.updatePtdpn(data).then((res) => {
            if (res.ok) {
                contentGlobalSuccess.next('common.updated');
            }
        });
    }

    protected historyModal(event: Event) {
        this.dialogService.open(
            FolderSickNotesSubmissionHistoryModalComponent,
            {
                header: translate.instant('sickNotes.submissionHistory.title'),
                styleClass: 'p-dialog-width-80',
            },
        );
    }

    protected reportModal(event: Event) {
        this.dialogService.open(FolderSickNotesDoctorReportModalComponent, {
            header: translate.instant('sickNotes.doctorReport.title'),
            styleClass: 'p-dialog-width-60',
        });
    }

    protected requestModal(event: Event) {
        this.dialogService.open(FolderSickNotesDoctorRequestModalComponent, {
            header: translate.instant('sickNotes.doctorRequest.title'),
            styleClass: 'p-dialog-width-60',
        });
    }

    protected async saveProfession() {
        const data: UpdatePersonInput = {
            personId: this.folder()!.person.id,
            profession: this.form.value.profession,
        };
        await this.store.api.updatePerson(data);
        contentGlobalSuccess.next('common.updated');
    }

    private getDecisionNumber() {
        this.store.api
            .getDecisionNumber({ id: this.folder()!.serviceId })
            .then((response) => {
                if (!response?.number) {
                    this.form.disable();
                    this.errorMessage.set(translate.instant(
                        'sickNotes.error.decisionNumber',
                    ));
                } else {
                    this.form.patchValue({
                        decisionNumber: response.number,
                    });
                }
            });
    }

    private getEmployers(folder: FolderGridItemObject) {
        this.store.api
            .getEmployers({
                serviceId: folder.serviceId,
                insuranceNumber: folder.person.insuranceNumber || '',
            })
            .then((response) => {
                if (response) {
                    this.form.patchValue({
                        employerName: response.employers[0]?.name,
                        employerAddress:
                            response.employers[0]?.address &&
                            mapAddress(response.employers[0]?.address),
                    });
                }
            });
    }

    private formPartOne(): FolderSickNotesCreateInput | undefined {
        if (this.form.isInvalid()) return;

        return {
            folderId: this.folder()!.id,
            serviceId: this.folder()!.serviceId,
            sszCode: this.form.value.sszCode,
            insuranceAdministrator: this.form.value.insuranceAdministrator,
            decisionNumber: this.form.value.decisionNumber,
            correctiveFilling: this.form.value.correctiveFilling,
            name: this.form.value.name,
            surname: this.form.value.surname,
            titles: this.form.value.titles,
            insuranceNumber: this.form.value.insuranceNumber,
            birthDate: csszDateFormat(this.form.value.birthDate),
            address: this.form.value.address,
            addition: this.form.value.addition,
            phone: this.form.value.phone,
            email: this.form.value.email,
            employmentId: this.form.value.employmentId,
            employerName: this.form.value.employerName,
            employerVariableSymbol: this.form.value.employerVariableSymbol,
            profession: this.form.value.profession,
            employerAddress: this.form.value.employerAddress,
            walkoutsFromDate: csszDateFormat(this.form.value.walkoutsFromDate),
            walkoutsFrom1: csszTimeFormat(this.form.value.walkoutsFrom1),
            walkoutsTo1: csszTimeFormat(this.form.value.walkoutsTo1),
            walkoutsFrom2: csszTimeFormat(this.form.value.walkoutsFrom2),
            walkoutsTo2: csszTimeFormat(this.form.value.walkoutsTo2),
            issueDate: csszDateFormat(this.form.value.issueDate),
            incapacityStartDate: csszDateFormat(
                this.form.value.incapacityStartDate,
            ),
            diagnosisCode: this.form.value.diagnosisCode,
            doctorOrigin: this.form.value.doctorOrigin,
            injury: this.form.value.injury,
            workInjury: this.form.value.workInjury,
            injuryFromAnotherPerson: this.form.value.injuryFromAnotherPerson,
            usingAlcoholOrDrugs: this.form.value.usingAlcoholOrDrugs,
            controlDate:
                csszDateFormat(this.ptdpnForm.controls.controlDate.value) ||
                null,
        } as FolderSickNotesCreateInput;
    }

    private formPartTwo(data: FolderSickNotesCreateInput): FolderSickNotesCloseInput | undefined {
        if (this.finalForm.isInvalid()) return;
        return {
            ...data,
            incapacityEndDate: csszDateFormat(this.finalForm.value.incapacityEndDate)!,
            finalDiagnosisCode: this.finalForm.value.finalDiagnosisCode!,
            issueEndDate: csszDateFormat(this.finalForm.value.issueEndDate)!,
            decidingDoctor: this.finalForm.value.decidingDoctor!,
        };
    }

    protected readonly csszInsuranceAdministratorEnumOptions =
        csszInsuranceAdministratorEnumOptions;

    private onInsuranceAdministratorChange(
        value: CsszInsuranceAdministratorEnum,
    ) {
        switch (value) {
            case CsszInsuranceAdministratorEnum.BEZPECNOSTNI_SBORY:
                this.prefillAddress(
                    'Nad Štolou',
                    '936',
                    '3',
                    'Praha 7',
                    '17034',
                    'Bezpečnostní sbory ČR',
                    50.09847,
                    14.42203,
                );
                break;
            case CsszInsuranceAdministratorEnum.MINISTERSTVO_OBRANY:
                this.prefillAddress(
                    'Náměstí Svobody',
                    '471',
                    '4',
                    'Praha 6',
                    '16001',
                    'Ministerstvo obrany',
                    50.10057,
                    14.3997,
                );
                break;
            case CsszInsuranceAdministratorEnum.VEZENSKA_SLUZBA:
                this.prefillAddress(
                    'Soudní',
                    '1672',
                    '1a',
                    'Praha 4',
                    '14000',
                    'Vězeňská služba ČR',
                    50.05817,
                    14.43744,
                );
                break;
            case CsszInsuranceAdministratorEnum.GENERALNI_REDITELSTVI_CEL:
                this.prefillAddress(
                    'Budějovická',
                    '1387',
                    '7',
                    'Praha 4',
                    '14000',
                    'Generální ředitelství cel',
                    50.0478,
                    14.44507,
                );
                break;
            default:
                break;
        }
    }

    private prefillAddress(
        street: string,
        cp: string,
        co: string,
        city: string,
        zip: string,
        employer: string,
        lat: number,
        lon: number,
    ) {
        const regionalStructure: RegionalStructureObject[] = [
            { name: `${cp}/${co}`, type: 'regional.address', isoCode: null },
            { name: street, type: 'regional.street', isoCode: null },
            { name: city, type: 'regional.municipality', isoCode: null },
            { name: 'Česko', type: 'regional.country', isoCode: 'CZ' },
        ];

        const employerAddress: AddressDetailObject = {
            name: `${street} ${cp}/${co}, ${city}`,
            location: `${city}, Česko`,
            position: { lat, lon },
            regionalStructure,
            zip,
        };

        this.form.patchValue({
            employerAddress,
            employerName: employer,
            profession: 'Příslušník',
        });
    }

    protected readonly routeParams = routeParams;
}
